import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { FormContext } from './form-context';
import cloneDeep from 'lodash/cloneDeep';

const prefixClassName = 'ten-form';

const LAYOUT = {
  INLINE: 'inline',
};
const emptyFn = () => {};

/**
 * @visibleName Form 表单
 */
class Form extends React.Component {
  static propTypes = {
    /**
     * 标签宽度
     */
    labelWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * 标签对其方式
     */
    labelAlign: PropTypes.oneOf(['right', 'left', 'center']),
    /**
     * 布局方式
     */
    layout: PropTypes.oneOf(['inline']),
    /**
     * 表单项目列表
     */
    model: PropTypes.object,
    /**
     * 表单默认值，重置表单时会被使用，如未提供，创建时 model 的值将被当做表单默认值
     */
    defaultModel: PropTypes.object,
    /**
     * 定义规则
     */
    rules: PropTypes.object,
    /**
     * 是否滚动到错误位置
     */
    errorLocation: PropTypes.bool,
    /**
     * 滚动到错误位置偏移
     */
    errorLocationOffset: PropTypes.number,
  };

  static defaultProps = {
    errorLocationOffset: 100,
  };

  constructor(props) {
    super(props);
    this.fields = [];
    this.formDefaultModel = props.defaultModel || cloneDeep(props.model);
  }

  /**
   * 重置表单，默认使用初始化时 model 的值进行重置
   * 如提供 defaultModel 属性则使用该值进行重置
   * @public
   */
  resetFields() {
    if (this.formDefaultModel) {
      this.fields.forEach((field) => field.resetField());
    }
  }

  /**
   * 清除表单验证错误
   * @public
   * @param {Array} props 需要清除的字段
   */
  clearValidate(props = []) {
    // eslint-disable-next-line
    const fields = props.length
      ? typeof props === 'string'
        ? this.fields.filter((field) => props === field.prop)
        : this.fields.filter((field) => props.indexOf(field.prop) > -1)
      : this.fields;
    fields.forEach((field) => {
      field.clearValidate();
    });
  }

  /**
   * 执行 form 验证
   * 支持两种使用方式 promise 和 回调函数
   * @public
   * @param {Function} callback 验证回调回调参数为(isValid, errors)
   */
  validate(callback, _errorLocation) {
    const props = this.fields.map((field) => field.props.prop);
    let errorLocation;
    if (typeof _errorLocation === 'undefined') {
      errorLocation = this.props.errorLocation; // eslint-disable-line
    }
    return this.validateFields(props, callback, errorLocation);
  }

  /**
   * 验证某个或某几个 formItem
   * @public
   * @param {String|Array} props 待验证的 props 可以是单个 prop 或者多个
   * @param {Function} callback 回调
   * @param {Boolean} errorLocation 同 errorLocation 属性
   * @returns {Promise} 返回验证 promise
   */
  validateFields(props = [], callback, errorLocation) {
    const { model, errorLocationOffset } = this.props;

    let propsToValidate = props;
    if (!Array.isArray(props)) {
      propsToValidate = [propsToValidate];
    }

    const promise = new Promise((resolve, reject) => {
      let valid = true;
      let firstErrorField;
      const errors = [];
      let count = 0;
      const fieldsToValidate = propsToValidate
        .map((prop) => this.fields.find((field) => field.props.prop === prop))
        .filter((v) => v);
      const total = fieldsToValidate.length;

      if (!model || total === 0) {
        resolve();
        callback(valid, null);
      }

      const checkValidateAll = () => {
        count += 1;
        if (count === total) {
          if (!valid) {
            if (errorLocation) {
              const errorElem = firstErrorField.$el;
              const top = errorLocationOffset || 0;
              const scrollY = errorElem.getBoundingClientRect().top + window.pageYOffset - top;
              window.scrollTo(0, scrollY);
            }
            callback && callback(valid, errors);
            reject(errors);
          } else {
            resolve();
            callback && callback(valid, null);
          }
        }
      };

      fieldsToValidate.forEach((field) => {
        field
          .validate()
          .then(() => {
            checkValidateAll();
          })
          .catch((errs) => {
            const error = { prop: field.props.prop, message: errs[0].message };
            valid = false;
            firstErrorField = firstErrorField || field;
            errors.push(error);
            checkValidateAll();
          });
      });
    });
    promise.catch(emptyFn);
    return promise;
  }

  shouldComponentUpdate(nextProps) {
    // hack vue watch
    const nextPropsRules = JSON.stringify(nextProps.rules);
    const currPropsRules = JSON.stringify(this.props.rules);
    if (nextPropsRules !== currPropsRules) {
      this.fields.forEach((field) => field.updateRules());
    }
    return true;
  }

  render() {
    const { props } = this;
    const {
      style,
      className: propClassName,
      children,
      layout,
      labelWidth,
      labelAlign,
      errorLocationOffset,
      ...other
    } = props;
    const className = classNames(propClassName, prefixClassName, [`${prefixClassName}--labelalign-${labelAlign}`], {
      [`${prefixClassName}--inline`]: layout === LAYOUT.INLINE,
    });

    const provideValue = {
      ...this.props,
      formDefaultModel: this.formDefaultModel,
      formItemAdd: (field) => {
        if (field) this.fields.push(field);
        return false;
      },
      formItemRemove: (field) => {
        if (field.prop) this.fields.splice(this.fields.indexOf(field), 1);
        return false;
      },
    };

    return (
      <FormContext.Provider value={provideValue}>
        <form className={className} style={style} {...other}>
          {children}
        </form>
      </FormContext.Provider>
    );
  }
}

export default Form;
